import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import BulkUpload from '../Commons/BulkUpload';

import { ArrowDownIcon, CalendarIcon, DSOIcon } from './Styles/Icons';
import { FlexRow, Bold, LocalizationProvider, DateRangePicker, FilterButton, Search, Popover, PopoverBody, 
	PopoverTitle, PopoverItem, ButtonGroup } from './Styles/UpdateTrackingInfo';

import { updateErrorMessage, updateSuccessMessage, updateMessage, updateError } from '../../actions/commonActions';
import { setUploadOrdersFileAction, setUploadOrdersFileBatch, uploadFilledTemplateFile, downloadOrderItems } from '../../actions/ordersActions';
import { fetchSPJBatchJobs, fetchSPJBatchMetric, clearSPJFailed, downloadSPJErrors } from '../../actions/spjActions';

import { SPJ_ACTIONS } from '../../commons/utils';

const PopoverIds = {
	FILTER_BY_DSO: 'filter-by-dso',
};
class UpdateTrackingInfo extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			dsoPopoverAnchorElement: null, 
			dsoSearchText: "", 
			dsoPopoverItem: "", 
			dateRange: [null, null],
			dateRangePickerOpen: false,
		};
	}

	async componentDidMount() {}

	handlePopoverClick = async (popoverId, event) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({ dsoPopoverAnchorElement: event.currentTarget });
				break;

			default:
				break;
		}
	};

	handlePopoverSelect = async (popoverId, choice) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({ dsoPopoverItem: choice.value, pageNumber: 0 });
				break;

			default:
				break;
		}
		this.handlePopoverClose(popoverId);
	};

	handlePopoverClose = async (popoverId) => {
		switch (popoverId) {
			case PopoverIds.FILTER_BY_DSO:
				this.setState({ dsoPopoverAnchorElement: null, dsoSearchText: '' });
				break;

			default:
				break;
		}
	};

	handleDateChange = (dateRange) => {
		this.setState({ dateRange: dateRange });
	};

	handleDateSelectOpen = () => {
		this.setState({ dateRangePickerOpen: true });
	};

	handleDateSelectClose = () => {
		this.setState({ dateRangePickerOpen: false });
	};

	downloadTemplateFile = async (action, onError) => {
		let { dateRange, dsoPopoverItem } = this.state;

		await this.props.downloadOrderItems (
			'download-order-items',
			"PENDING",
			dateRange[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : null,
			dateRange[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : null,
			null,
			dsoPopoverItem ? dsoPopoverItem : "", 
			"",
			true
		);
	};

	uploadFilledTemplateFile = async (action, formData, onSuccess, onError) => {
		await this.props.uploadFilledTemplateFile(action, formData, onSuccess, onError);
	};

	render() {
		let { dateRangePickerOpen, dateRange, dsoPopoverAnchorElement, dsoSearchText, dsoPopoverItem } = this.state;
		let { open, dsoOptions, uploadOrdersFileAction, uploadOrdersFileBatch, uploadOrdersFileType, shippingDetailsMandatory } = this.props;
		
		let filterByDSOLabel = (dsoOptions.find((option) => option.value === dsoPopoverItem) || {}).name || '';

		return open ? (
			<BulkUpload
				open={open}
				title={'Update Order Status And Tracking Info'}
				spjAction={SPJ_ACTIONS.SALES_ORDER_UPDATE}
				uploadFileAction={uploadOrdersFileAction || ''}
				uploadFileBatch={uploadOrdersFileBatch || ''}
				uploadFileType={uploadOrdersFileType || ''}
				onClose={this.props.toggle}
				onSuccess={this.props.onSuccess}
				disableDownload={!(dateRange && dateRange[0] && dateRange[1])}
				downloadTemplateFile={this.downloadTemplateFile}
				uploadFilledTemplateFile={this.uploadFilledTemplateFile}
				setUploadFileAction={this.props.setUploadOrdersFileAction}
				setUploadFileBatch={this.props.setUploadOrdersFileBatch}
			>
				<Popover
					id={PopoverIds.FILTER_BY_DSO}
					open={Boolean(dsoPopoverAnchorElement)}
					anchorEl={dsoPopoverAnchorElement}
					onClose={() => this.handlePopoverClose(PopoverIds.FILTER_BY_DSO)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
				>
					<PopoverTitle>Select DSO</PopoverTitle>

					<PopoverItem>
						<Search
							searchText={dsoSearchText}
							placeholder={'Search DSO'}
							searchOnChange={true}
							onSearchHandler={(value) => this.setState({ dsoSearchText: value })}
						/>
					</PopoverItem>

					<PopoverBody>
						{dsoOptions.map((item, index) => {
							return item.name.toUpperCase().includes((dsoSearchText || '').toUpperCase()) ? (
								<PopoverItem
									key={index}
									disabled={item.disabled || item.value === dsoPopoverItem}
									onClick={(e) => this.handlePopoverSelect(PopoverIds.FILTER_BY_DSO, item)}
								>
									{item.name}
								</PopoverItem>
							) : null;
						})}
					</PopoverBody>
				</Popover>

				<FlexRow style={{ margin: '0.5rem auto', textAlign: 'center' }}>
					Select the date range for which you wish to download the order file. Add required details and upload the filled XLSX to update multiple orders at once.
				</FlexRow>

				<FlexRow style={{ margin: '0.5rem auto', textAlign: 'center' }}>You can also select from available DSO options to filter orders in the file by DSO.</FlexRow>

				<FlexRow style={{ justifyContent: 'center', textAlign: 'center', paddingTop: '0.5rem' }}>
					<Bold>Rules:</Bold>
				</FlexRow>

				<FlexRow style={{ alignSelf: 'center', paddingBottom: '0.5rem' }}>
					<ul style={{ listStyle: 'circle' }}>
						<li>
							Do not alter <Bold>Order ID</Bold>, <Bold>Supplier</Bold>, <Bold>Quantity</Bold> and <Bold>Order Line</Bold>.
						</li>

						<li>
							<Bold>Status</Bold> can only have the following values: [<Bold>PROCESSING</Bold>, <Bold>FULFILLED</Bold>, <Bold>CANCELLED</Bold>].
						</li>

						<li>
							<Bold>Shipping Company</Bold> can only have the following values: [<Bold>FedEx</Bold>, <Bold>UPS</Bold>, <Bold>OnTrac</Bold>, <Bold>USPS</Bold>, <Bold>DHL</Bold>, <Bold>UDS</Bold>, <Bold>DPD</Bold>, <Bold>Royal Mail</Bold>, <Bold>Canpar Express</Bold>, <Bold>ATS</Bold>, <Bold>Couriers Plus</Bold>, <Bold>Purolator</Bold>, <Bold>Canada Post</Bold>, <Bold>Loomis</Bold>, <Bold>Bestway</Bold>, <Bold>OTHER</Bold>].
						</li>

						<li>
							<Bold>Shipping Company</Bold> and <Bold>Tracking Number</Bold> are {shippingDetailsMandatory ? 'required' : 'optional'} for status <Bold>FULFILLED</Bold>.
						</li>

						<li>
							If <Bold>Shipping Company</Bold> is <Bold>OTHER</Bold>, <Bold>Custom Courier Name</Bold> is required, and either <Bold>Tracking Number</Bold> or <Bold>Estimated Delivery Date</Bold> must be filled in.
						</li>

						<li>
							<Bold>Estimated Delivery Date</Bold> must be in <Bold>YYYY-MM-DD</Bold> format.
						</li>
					</ul>
				</FlexRow>

				<FlexRow>
					<ButtonGroup alignCenter>
						<LocalizationProvider>
							<DateRangePicker
								calendars={2}
								value={dateRange}
								open={dateRangePickerOpen}
								disableCloseOnSelect={false}
								disableFuture
								orientation={'landscape'}
								disableHighlightToday={true}
								onChange={(dateRange) => this.handleDateChange(dateRange)}
								onClose={() => this.handleDateSelectClose()}
								renderInput={(startProps, endProps) => (
									<FilterButton
										primary
										outline
										onClick={(e) => {
											this.handleDateSelectOpen();
										}}
									>
										<CalendarIcon />
										{dateRange && dateRange[0] && dateRange[1]
											? `${dateRange[0].toLocaleString('default', { month: 'short' })} ${dateRange[0].getDate()} - ${dateRange[1].toLocaleString('default', {
													month: 'short',
											  })} ${dateRange[1].getDate()}`
											: 'Select Date'}
										<ArrowDownIcon />
									</FilterButton>
								)}
							/>
						</LocalizationProvider>

						<FilterButton
							primary
							outline
							onClick={(e) => this.handlePopoverClick(PopoverIds.FILTER_BY_DSO, e)}
						>
							<DSOIcon />
							{filterByDSOLabel}
							<ArrowDownIcon />
						</FilterButton>
					</ButtonGroup>
				</FlexRow>
			</BulkUpload>
		) : null;
	}
}

const mapStateToProps = (state) => {
	return {
		uploadOrdersFileAction: state.orders.uploadOrdersFileAction || '',
		uploadOrdersFileBatch: state.orders.uploadOrdersFileBatch || '',
		uploadOrdersFileType: state.orders.uploadOrdersFileType || '',
		shippingDetailsMandatory: state?.supplier?.sellerPortalConfig?.orders?.shipping_details_mandatory !== false,
	};
};

const mapDispatch = {
	setUploadOrdersFileAction,
	downloadOrderItems,
	uploadFilledTemplateFile,
	setUploadOrdersFileBatch,
	fetchSPJBatchJobs,
	fetchSPJBatchMetric,
	clearSPJFailed,
	downloadSPJErrors,
	updateError,
	updateErrorMessage,
	updateMessage,
	updateSuccessMessage,
};

export default withRouter(connect(mapStateToProps, mapDispatch)(UpdateTrackingInfo));
